import React, { useState } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  holdings: Holding[];
}

export const BenchmarkComparison: React.FC<Props> = ({ holdings }) => {
  const { language } = useLanguage();
  const [selectedBenchmark, setSelectedBenchmark] = useState<'SPY' | 'MSCI' | 'GLD'>('SPY');
  const [timeframe, setTimeframe] = useState<'1M' | '6M' | '1Y' | 'YTD'>('1Y');

  // Calculation of portfolio metrics
  const totalVal = holdings.reduce((acc, h) => acc + (h.quantity * (h.current_price || h.avg_cost)), 0);
  const totalCost = holdings.reduce((acc, h) => acc + (h.quantity * h.avg_cost), 0);
  const portfolioReturnPct = totalCost > 0 ? ((totalVal - totalCost) / totalCost) * 100 : 18.4;

  // Benchmark returns mockup
  const benchmarkReturns = {
    SPY: { '1M': 2.1, '6M': 8.5, '1Y': 14.2, YTD: 11.3, name: 'S&P 500 (SPY)' },
    MSCI: { '1M': 1.8, '6M': 6.9, '1Y': 11.5, YTD: 9.1, name: 'MSCI World (URTH)' },
    GLD: { '1M': 3.4, '6M': 12.1, '1Y': 19.8, YTD: 15.6, name: 'Zlato / Gold (GLD)' }
  };

  const currentBenchmarkReturn = benchmarkReturns[selectedBenchmark][timeframe];
  const alphaPct = portfolioReturnPct - currentBenchmarkReturn;
  const betaRatio = 0.85; // Simulated low volatility Beta vs trh

  return (
    <div className="glass-panel p-6 space-y-6 font-mono text-xs">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-[var(--border-color)]">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
            <span>🏛️</span>
            {language === 'sk' ? 'VIP Benchmark Engine' : 'Institutional Benchmark Engine'}
          </h3>
          <p className="text-[10px] text-stone-500 mt-1">
            {language === 'sk' 
              ? 'Porovnanie výnosnosti portfólia voči trhovým indexom (Alpha & Beta audit)'
              : 'Compare portfolio performance against market benchmarks & compute Alpha/Beta metrics'}
          </p>
        </div>

        {/* Benchmark Selector */}
        <div className="flex items-center gap-2 bg-[var(--accent-color)] border border-[var(--border-color)] rounded-xl p-1">
          <button
            onClick={() => setSelectedBenchmark('SPY')}
            className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all ${selectedBenchmark === 'SPY' ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]'}`}
          >
            S&P 500
          </button>
          <button
            onClick={() => setSelectedBenchmark('MSCI')}
            className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all ${selectedBenchmark === 'MSCI' ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]'}`}
          >
            MSCI World
          </button>
          <button
            onClick={() => setSelectedBenchmark('GLD')}
            className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all ${selectedBenchmark === 'GLD' ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]'}`}
          >
            Zlato (GLD)
          </button>
        </div>
      </div>

      {/* Primary KPI Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card-premium">
          <span className="text-[9px] uppercase font-bold text-stone-500 block mb-1">
            {language === 'sk' ? 'Výnos Portfólia' : 'Portfolio Return'}
          </span>
          <span className="text-xl font-black text-brand-green">
            +{portfolioReturnPct.toFixed(2)}%
          </span>
        </div>

        <div className="stat-card-premium">
          <span className="text-[9px] uppercase font-bold text-stone-500 block mb-1">
            {benchmarkReturns[selectedBenchmark].name}
          </span>
          <span className="text-xl font-black text-stone-800">
            +{currentBenchmarkReturn.toFixed(2)}%
          </span>
        </div>

        <div className="stat-card-premium">
          <span className="text-[9px] uppercase font-bold text-stone-500 block mb-1">
            {language === 'sk' ? 'Nadvýnos (Alpha)' : 'Alpha Generation'}
          </span>
          <span className={`text-xl font-black ${alphaPct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
            {alphaPct >= 0 ? '+' : ''}{alphaPct.toFixed(2)}%
          </span>
        </div>

        <div className="stat-card-premium">
          <span className="text-[9px] uppercase font-bold text-stone-500 block mb-1">
            {language === 'sk' ? 'Koeficient Rizika (Beta)' : 'Volatility Beta'}
          </span>
          <span className="text-xl font-black text-indigo-700">
            {betaRatio.toFixed(2)} <span className="text-[9px] font-normal text-stone-500">(Nízke riziko)</span>
          </span>
        </div>
      </div>

      {/* Visual Chart Comparison Canvas Mockup */}
      <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 space-y-3">
        <div className="flex justify-between items-center text-[10px] font-bold text-stone-500 uppercase">
          <span>{language === 'sk' ? 'Porovnanie Výkonnosti (Krivka Výnosu)' : 'Performance Curve Comparison'}</span>
          <div className="flex gap-1">
            {(['1M', '6M', '1Y', 'YTD'] as const).map(tf => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-2 py-0.5 rounded text-[9px] font-bold ${timeframe === tf ? 'bg-[#1c1917] text-white' : 'text-stone-500 hover:bg-stone-200'}`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>

        {/* SVG Graphic Comparison Curve */}
        <div className="w-full h-36 relative flex items-center justify-center">
          <svg className="w-full h-full overflow-visible" viewBox="0 0 400 100">
            {/* Grid Lines */}
            <line x1="0" y1="20" x2="400" y2="20" stroke="#e7e5e4" strokeDasharray="3 3" />
            <line x1="0" y1="50" x2="400" y2="50" stroke="#e7e5e4" strokeDasharray="3 3" />
            <line x1="0" y1="80" x2="400" y2="80" stroke="#e7e5e4" strokeDasharray="3 3" />

            {/* Benchmark Line (Gray/Blue) */}
            <path
              d="M 0 75 Q 100 65, 200 45 T 400 35"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2.5"
              strokeDasharray="4 4"
            />

            {/* Portfolio Line (Green / Bold) */}
            <path
              d="M 0 80 Q 100 50, 200 30 T 400 15"
              fill="none"
              stroke="#15803d"
              strokeWidth="3.5"
            />
          </svg>
        </div>

        <div className="flex justify-center gap-6 text-[10px] font-extrabold pt-2 border-t border-stone-200/80">
          <div className="flex items-center gap-2 text-emerald-800">
            <span className="w-3 h-1 bg-emerald-700 rounded-full"></span>
            <span>{language === 'sk' ? 'Moje Portfólio' : 'My Portfolio'} (+{portfolioReturnPct.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center gap-2 text-stone-500">
            <span className="w-3 h-1 bg-slate-400 rounded-full border border-dashed"></span>
            <span>{benchmarkReturns[selectedBenchmark].name} (+{currentBenchmarkReturn.toFixed(1)}%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};
